import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { initialsFor } from '../ChatLayout/ListRow';
import { Storage } from '../../config';
import { DARK_MODE_EVENT, ROUTES, STORAGE_KEYS } from '../../config/constants';

// ICONS IMPORT
import { BiSolidMessageRounded } from 'react-icons/bi';
import { BiSolidPhoneCall } from 'react-icons/bi';
import { IoNotificationsSharp } from 'react-icons/io5';
import { FaBookmark } from 'react-icons/fa';
import { useDarkModeValue } from '../../hooks/useDarkModeValue';
import { MdDarkMode, MdLightMode, MdSettings } from 'react-icons/md';

/**
 * Navigation rail.
 *
 * Destinations, order and the two unread counters are unchanged; only the
 * presentation follows the design: a dark rail of icon pills, with the active
 * route marked by a tinted background plus an accent indicator bar, and
 * counters rendered as corner badges.
 */

const NAV_ITEMS = [
  {
    to: ROUTES.MESSAGES,
    label: 'Messages',
    Icon: BiSolidMessageRounded,
    prefix: ROUTES.MESSAGES,
    counter: 'messages',
  },
  {
    to: ROUTES.CALLS,
    label: 'Calls',
    Icon: BiSolidPhoneCall,
    prefix: '/calls',
    counter: 'missedCalls',
  },
  {
    to: ROUTES.SAVED,
    label: 'Saved',
    Icon: FaBookmark,
    prefix: ROUTES.SAVED,
  },
  {
    to: ROUTES.NOTIFICATIONS,
    label: 'Notifications',
    Icon: IoNotificationsSharp,
    prefix: ROUTES.NOTIFICATIONS,
  },
];

// Settings sits at the very foot of the rail, under the theme switch.
const SETTINGS_ITEM = {
  to: ROUTES.SETTINGS_PROFILE,
  label: 'Settings',
  Icon: MdSettings,
  prefix: ROUTES.SETTINGS,
};

/**
 * A destination stays lit for everything beneath it — opening a conversation
 * (/messages/:id) or a call detail is still that section. Matching on the exact
 * pathname meant the rail went dark the moment you selected anything.
 *
 * The boundary check stops '/messages' from also claiming a '/messages-archive'.
 */
const isUnder = (pathname, prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`);

/**
 * Geometry of one nav slot, in one place.
 *
 * The active indicator is a single element that slides between destinations, so
 * its travel per step has to equal the real pitch of the list. Both numbers feed
 * the styles below as well, which is what stops the indicator from drifting out
 * of alignment the first time someone adjusts the item height.
 */
const ITEM_H = 46;
const ITEM_GAP = 4;
const PITCH = ITEM_H + ITEM_GAP;

const railStyles = {
  nav: {
    width: '100%',
    // Fills the rail, which is itself sized against the shell. Pinned to 100vh
    // it stood taller than the shell wherever the visual viewport is shorter
    // than the layout viewport — every mobile browser with a collapsing URL bar.
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    padding: '16px 0 14px',
  },
  brand: {
    width: '42px',
    height: '42px',
    flex: '0 0 auto',
    borderRadius: '14px',
    overflow: 'hidden',
    background: 'var(--accent)',
    color: 'var(--accent-fg)',
    display: 'grid',
    placeItems: 'center',
    fontWeight: 800,
    fontSize: '15px',
    letterSpacing: '-0.02em',
    marginBottom: '16px',
    textDecoration: 'none',
  },
  item: {
    position: 'relative',
    width: '50px',
    height: `${ITEM_H}px`,
    flex: '0 0 auto',
    borderRadius: '15px',
    display: 'grid',
    placeItems: 'center',
    transition: 'background-color 0.16s ease, color 0.16s ease',
  },
  itemCompact: {
    width: '42px',
    height: '40px',
    borderRadius: 'var(--r-lg)',
  },
  // One indicator for the whole list, parked in the `<ul>` and translated to the
  // active slot — it used to be rendered inside each active item, so it vanished
  // from one row and reappeared in another with nothing in between.
  indicator: {
    position: 'absolute',
    left: '-13px',
    top: '12px',
    width: '3px',
    height: '22px',
    borderRadius: '0 3px 3px 0',
    background: 'var(--accent)',
    pointerEvents: 'none',
  },
  indicatorCompact: {
    position: 'absolute',
    left: '-17px',
    top: '11px',
    width: '3px',
    height: '18px',
    borderRadius: '0 3px 3px 0',
    background: 'var(--accent)',
  },
  // Counters are alerts, not brand — on the accent they were indistinguishable
  // from the active-route tint sitting directly under them.
  badge: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    minWidth: '18px',
    height: '18px',
    padding: '0 5px',
    borderRadius: '9px',
    background: 'var(--danger)',
    color: '#fff',
    fontSize: '10px',
    fontWeight: 800,
    lineHeight: 1,
    display: 'grid',
    placeItems: 'center',
    border: '2px solid var(--rail)',
  },
};

const SideBar = () => {
  const { isDarkMode } = useDarkModeValue();
  const profile = useSelector((state) => state?.AppReducer?.user_profile?.data);
  const me = profile || Storage.getCachedUser();

  const avatarUrl = me?.media?.media_thumb_url || me?.media?.media_url || null;
  const initials = initialsFor(me?.first_name || me?.nick_name || '');
  const count_messages = useSelector((state) => state?.MsgReducer?.unread_message_count);
  const count_missed_call = useSelector((state) => state?.MsgReducer?.missedcallcount);

  const location = useLocation();
  const currentLocation = location?.pathname;

  const counters = {
    messages: count_messages,
    missedCalls: count_missed_call,
  };

  const badgeValue = (key) => {
    if (!key) return null;
    const raw = Number(counters[key]);
    if (!Number.isFinite(raw) || raw <= 0) return null;
    return raw > 99 ? '99+' : String(raw);
  };

  /**
   * Theme switch. Writes the same `darkMode` key App.js reads on boot and
   * fires the `darkModeToggled` event that App.js and useDarkModeValue
   * already listen for, so the `.dark` class and every token follow.
   */
  const toggleTheme = () => {
    Storage.writeJSON(STORAGE_KEYS.DARK_MODE, !isDarkMode);
    window.dispatchEvent(new Event(DARK_MODE_EVENT));
  };

  // Which slot the sliding indicator parks in. -1 on a route outside the rail.
  const activeIndex = NAV_ITEMS.findIndex((item) => isUnder(currentLocation, item.prefix));

  const renderItem = ({ to, label, Icon, counter, prefix }, { compact } = {}) => {
    const isActive = isUnder(currentLocation, prefix);
    const badge = badgeValue(counter);

    return (
      <Link
        key={to}
        to={to}
        title={label}
        aria-label={label}
        aria-current={isActive ? 'page' : undefined}
        style={{
          ...railStyles.item,
          ...(compact ? railStyles.itemCompact : null),
          background: isActive ? 'rgba(255, 255, 255, 0.14)' : 'transparent',
          color: isActive ? 'var(--rail-fg)' : 'var(--rail-muted)',
        }}
        onMouseEnter={(e) => {
          if (isActive) return;
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
          e.currentTarget.style.color = 'var(--rail-fg)';
        }}
        onMouseLeave={(e) => {
          if (isActive) return;
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = 'var(--rail-muted)';
        }}
      >
        {/* The list's indicator slides (see the `<ul>` below); the settings item
            sits in its own group at the foot of the rail with nothing to slide
            between, so it keeps a static one. */}
        {compact && isActive && <span style={railStyles.indicatorCompact} />}
        <Icon size={compact ? 18 : 20} color="currentColor" />
        {badge && (
          // Keyed on the value so an increment remounts the node and replays the
          // pop — a count that changes silently is the easiest thing to miss.
          <span key={badge} className="badge-pop" style={railStyles.badge}>
            {badge}
          </span>
        )}
      </Link>
    );
  };

  return (
    <nav style={railStyles.nav} aria-label="Primary">
      {/* Your avatar doubles as the way back to the conversation list. */}
      <Link
        to={ROUTES.MESSAGES}
        title="Messages"
        aria-label="Go to messages"
        style={railStyles.brand}
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            style={{ width: '100%', height: '100%', borderRadius: 'inherit', objectFit: 'cover' }}
          />
        ) : (
          initials
        )}
      </Link>

      <ul
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: `${ITEM_GAP}px`,
        }}
      >
        {/* Kept mounted at all times and moved with `transform`, which is what
            makes the travel animatable. It is faded out rather than unmounted on
            a route with no rail entry (e.g. an in-call screen), so returning does
            not restart it from the top of the list. */}
        <span
          className="rail-indicator"
          aria-hidden="true"
          style={{
            ...railStyles.indicator,
            transform: `translateY(${Math.max(activeIndex, 0) * PITCH}px)`,
            opacity: activeIndex >= 0 ? 1 : 0,
          }}
        />

        {NAV_ITEMS.map((item) => (
          <li key={item.to} style={{ listStyle: 'none' }}>
            {renderItem(item)}
          </li>
        ))}
      </ul>

      <div style={{ flex: 1 }} />

      {/* Foot of the rail: theme switch, then settings at the very bottom. */}
      <button
        type="button"
        onClick={toggleTheme}
        title={isDarkMode ? 'Switch to light theme' : 'Switch to dark theme'}
        aria-label="Toggle theme"
        // Resting state matches an INACTIVE nav item: the standing tint plus
        // full-strength foreground made the switch read as a selected
        // destination, which it never is.
        style={{
          ...railStyles.item,
          ...railStyles.itemCompact,
          border: 0,
          background: 'transparent',
          color: 'var(--rail-muted)',
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
          e.currentTarget.style.color = 'var(--rail-fg)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = 'var(--rail-muted)';
        }}
      >
        {isDarkMode ? <MdLightMode size={18} /> : <MdDarkMode size={18} />}
      </button>

      {renderItem(SETTINGS_ITEM, { compact: true })}
    </nav>
  );
};

export default SideBar;
